import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Shield, ChevronLeft, MessageCircleMore, MessageCircle, Mail, MessageSquareText,
  Bot, KeyRound, Lock, FileText, Receipt, Settings as SettingsIcon,
  Eye, EyeOff, Copy, X, ExternalLink, CheckCircle2, XCircle,
  Wallet, Plus, History, Loader2, Clock, Package,
  CreditCard, Smartphone, Landmark, ChevronDown, Sparkles,
  Calendar as CalendarIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import BackupsPanel from "./BackupsPanel";
import SmartBackupCalendar from "./SmartBackupCalendar";
import CreditCenter from "./credits/CreditCenter";
import CreditPricingPackages from "./credits/CreditPricingPackages";
import SmartBackupCompact from "./credits/SmartBackupCompact";
import BillingHistoryModal from "./credits/BillingHistoryModal";
import {
  getCredits, listCreditOperations, listCreditTopups, requestTopup,
  TOPUP_METHODS, formatCredits, formatNis, creditsToNis, CREDIT_TO_NIS,
} from "../lib/credits";
import { api } from "../lib/api";
import { packageLabel } from "../lib/packages";

/**
 * "כלי אדמין" sidebar — appears in EVERY client dashboard for the logged-in
 * user (owner OR super-admin). Shows ONLY these client-facing sections:
 *   1. כמות קרדיטים  — current balance + pending top-up notice
 *   2. הוסף קרדיטים  — request a top-up (pending until super-admin approves)
 *   3. היסטוריית חיובים  — clean log of credits added & deducted
 *   4. תוספים זמינים  — read-only status of which add-ons are active
 *   5. כספת מפתחות וסיסמאות  — vault of the company's API keys
 *
 * Super-admin tools (work session start/end, manual top-up, adjust,
 * approval queue, AI cost calculations × 2) live in a SEPARATE popup on
 * the `/S.ADMIN` dashboard ("ניהול עבודה וקרדיטים" button per company cube),
 * so the client never sees the underlying ×2 math.
 */

const ADDONS = [
  { key: "whatsapp_automation", label: "חיבור אוטמציה וואטסאפ", icon: MessageCircleMore, color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30" },
  { key: "email_automation", label: "חיבור אוטמציה מיילים", icon: Mail, color: "text-sky-300", bg: "bg-sky-500/10 border-sky-500/30" },
  { key: "sms_automation", label: "חיבור אוטומציה SMS", icon: MessageSquareText, color: "text-indigo-300", bg: "bg-indigo-500/10 border-indigo-500/30" },
  { key: "ai_whatsapp_bot", label: "בוט וואטסאפ חכם", icon: Bot, color: "text-fuchsia-300", bg: "bg-fuchsia-500/10 border-fuchsia-500/30" },
];

export function ClientAdminSidebar({ company }) {
  const { user } = useAuth();
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [smartBackupOpen, setSmartBackupOpen] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);

  const [balance, setBalance] = useState(null);
  const [pendingTopups, setPendingTopups] = useState([]);   // for THIS company only
  const [topupOpen, setTopupOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [debt, setDebt] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const companyId = company?.id;

  const reload = async () => {
    if (!companyId) return;
    try {
      const [b, tps, dRes] = await Promise.all([
        getCredits(companyId),
        listCreditTopups(companyId, 50),
        api.get(`/companies/${companyId}/credits/debt`).then(r => r.data).catch(() => null),
      ]);
      setBalance(b);
      setPendingTopups(tps.filter((t) => t.status === "pending"));
      setDebt(dRes);
    } catch {}
  };

  useEffect(() => {
    if (open && companyId) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  // Listen for cross-component request to open the top-up dialog (e.g.
  // dispatched from the brief wizard's pricing screen after "סיים ושמור").
  useEffect(() => {
    const handler = () => { setOpen(true); setTopupOpen(true); };
    window.addEventListener("flowup:open-topup", handler);
    return () => window.removeEventListener("flowup:open-topup", handler);
  }, []);

  if (!user) return null;   // not logged in at all
  const enabledAddons = balance?.addons_enabled || company?.addons_enabled || {};
  const has = (k) => (company?.channels || []).includes(k);
  const hasInvoices = has("email") || has("whatsapp");

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} הועתק`); }
    catch { toast.error("לא הצליח להעתיק"); }
  };

  const openHistory = async () => {
    setShowHistory(true);
    if (!companyId) return;
    setHistoryLoading(true);
    try {
      const [ops, tps] = await Promise.all([
        listCreditOperations(companyId, 100),
        listCreditTopups(companyId, 100),
      ]);
      // Build a clean unified ledger — NO mention of ×2, AI, etc.
      const items = [
        ...ops.map((o) => ({
          id: `op-${o.id}`,
          created_at: o.created_at,
          type: "charge",
          delta: -Math.abs(o.charged_credits),  // negative = deduction
          label: o.description || "חיוב",
          activities: Array.isArray(o.activities) ? o.activities : [],
        })),
        ...tps.filter((t) => t.status === "approved").map((t) => ({
          id: `tp-${t.id}`,
          created_at: t.approved_at || t.created_at,
          type: "topup",
          delta: t.credits,
          label: `טעינת קרדיטים`,
          activities: [],
        })),
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setHistory(items);
    } finally { setHistoryLoading(false); }
  };

  const balanceNum = balance?.credit_balance ?? 0;
  const pendingTotal = pendingTopups.reduce((s, t) => s + t.credits, 0);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 left-4 z-[9997] inline-flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-l from-indigo-600 to-fuchsia-600 text-white font-bold text-sm shadow-2xl shadow-indigo-500/40 hover:scale-105 transition"
        data-testid="client-admin-sidebar-toggle"
      >
        <Shield className="h-4 w-4" />
        כלי אדמין
        {pendingTopups.length > 0 && (
          <span className="bg-amber-400 text-stone-900 text-[10px] font-extrabold rounded-full px-1.5 py-0.5">{pendingTopups.length}</span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="client-admin-sidebar-backdrop" />
      )}

      <aside
        className={`fixed top-0 bottom-0 left-0 z-[9999] w-full sm:w-[420px] bg-stone-900 border-l-2 border-fuchsia-500/30 shadow-2xl transition-transform overflow-y-auto ${open ? "translate-x-0" : "-translate-x-full"}`}
        dir="rtl"
        data-testid="client-admin-sidebar"
      >
        <header className="sticky top-0 bg-stone-900 border-b border-white/10 px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-fuchsia-400" />
            <div>
              <h3 className="font-heading text-lg font-extrabold text-white">כלי אדמין</h3>
              <p className="text-xs text-stone-400">{company?.display_name || company?.name || slug}</p>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-stone-300 hover:bg-white/10 transition" data-testid="client-admin-sidebar-close">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="px-5 py-5 space-y-5">
          {/* Unified Credit Center — merged balance + buy CTA + history.
              Replaces the previous red balance card + green Add button. */}
          <section data-testid="section-credit-center">
            <CreditCenter
              balance={balance}
              pendingTopups={pendingTopups}
              companyId={companyId}
              onBuy={() => setPricingOpen(true)}
              onHistory={() => setShowHistory(true)}
            />
          </section>

          {/* גיבויים חכמים — single compact button. Modal lifted up to
              the parent so we can close the side-drawer before showing
              the calendar (cleaner UX, no stacked layers). */}
          {company?.id && (
            <section data-testid="section-smart-backups">
              <SmartBackupCompact
                companyId={company.id}
                onOpen={() => { setOpen(false); setSmartBackupOpen(true); }}
              />
            </section>
          )}

          {/* FlowUp — single product button. Click → /flowup which mounts
              the WaSender suite (Dashboard, Devices, Contacts, Templates,
              Campaigns, etc). Replaces the old 3 product CTAs. */}
          {company?.id && (
            <section data-testid="flowup-section" className="space-y-2">
              <a
                href="/flowup"
                onClick={() => setOpen(false)}
                className="group relative block w-full rounded-xl bg-gradient-to-br from-[#1171ef] via-[#06b6d4] to-[#11cdef] hover:opacity-95 active:scale-[0.99] shadow-lg shadow-cyan-500/30 ring-1 ring-cyan-300/50 transition-all overflow-hidden p-1.5"
                data-testid="open-flowup-btn"
                aria-label="FlowUp"
              >
                <div className="relative h-24 w-full rounded-lg bg-white flex items-center justify-center overflow-hidden">
                  <img
                    src="/brand/flowupsender-logo.png"
                    alt="FlowUp"
                    className="h-full w-full object-contain scale-110 pointer-events-none select-none"
                    onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.innerHTML += '<span class="text-[28px] font-extrabold text-[#1171ef] tracking-tight">FlowUp</span>'; }}
                  />
                </div>
                <div className="px-1 pt-1.5 pb-0.5 text-[11px] text-white text-center leading-snug font-extrabold pointer-events-none">
                  פתחי את <span className="text-cyan-100">FlowUp</span> — וואצאפ · קמפיינים · בוטים
                </div>
              </a>
            </section>
          )}

          {/* WhatsApp VIP customer-service — always available in every
              client's admin tools. Opens wa.me with a prefilled Hebrew
              greeting. Owner's rule (2026-05-20): same number that
              services bank-transfer purchases — 0504831000. */}
          <section data-testid="section-vip-wa">
            <button
              type="button"
              onClick={() => {
                const msg = encodeURIComponent("שלום, אני זקוק/ה לעזרה במערכת FlowUp");
                window.open(`https://wa.me/972504831000?text=${msg}`, "_blank");
              }}
              className="group w-full rounded-xl border border-emerald-400/40 bg-gradient-to-br from-emerald-600 via-emerald-500 to-emerald-600 hover:opacity-95 active:scale-[0.99] py-2.5 px-3 text-right shadow-md shadow-emerald-500/20 transition-all"
              data-testid="vip-wa-btn"
            >
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-extrabold text-white leading-tight">שירות לקוחות VIP</div>
                  <div className="text-[10px] text-white/85 mt-0.5">WhatsApp · 0504831000</div>
                </div>
              </div>
            </button>
          </section>
        </div>
      </aside>

      {topupOpen && companyId && (
        <AddCreditsModal
          companyId={companyId}
          onClose={() => setTopupOpen(false)}
          onSuccess={async () => { setTopupOpen(false); await reload(); }}
        />
      )}
      {pricingOpen && companyId && (
        <CreditPricingPackages
          companyId={companyId}
          prefilledDebt={debt}
          onClose={() => setPricingOpen(false)}
          onSuccess={async () => { setPricingOpen(false); await reload(); }}
        />
      )}
      {showHistory && companyId && (
        <BillingHistoryModal
          companyId={companyId}
          onClose={() => setShowHistory(false)}
          onBuy={() => { setShowHistory(false); setPricingOpen(true); }}
        />
      )}
      {smartBackupOpen && company?.id && (
        <SmartBackupCalendar
          companyId={company.id}
          onClose={() => setSmartBackupOpen(false)}
        />
      )}
    </>
  );
}

// ============ Add Credits Modal ============
function AddCreditsModal({ companyId, onClose, onSuccess }) {
  const presets = [50, 100, 250, 500, 1000];
  const [credits, setCredits] = useState(100);
  const [method, setMethod] = useState("credit_card");
  const [note, setNote] = useState("");
  // Coupon discount — manual slider 0-100%, step 5%. Locked behind a
  // code that the user must enter & confirm before the slider unlocks.
  // The legitimate code is 1986 (hard-coded per shany's request).
  const [discount, setDiscount] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [couponUnlocked, setCouponUnlocked] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [busy, setBusy] = useState(false);

  const tryUnlockCoupon = () => {
    if (couponCode.trim() === "1986") {
      setCouponUnlocked(true);
      setCouponError("");
      toast.success("הקופון נפתח! בחר/י את אחוז ההנחה");
    } else {
      setCouponError("קוד לא תקין");
      toast.error("הקוד אינו תקין");
    }
  };

  const submit = async () => {
    if (credits <= 0) { toast.error("יש להזין כמות חיובית"); return; }
    setBusy(true);
    try {
      const fullNote = discount > 0
        ? `[קופון ${discount}%] ${note}`.trim()
        : note;
      // === Method routing ============================================
      // 1. instant_card_bit_paypal → CardCom hosted page (real charge)
      // 2. bank_transfer          → classic pending-approval flow
      // 3. other                  → defaults to pending-approval
      if (method === "instant_card_bit_paypal") {
        const finalNis = creditsToNis(credits) * (discount > 0 ? (1 - discount / 100) : 1);
        const { data } = await api.post(`/payments/cardcom/create-checkout`, {
          company_id: companyId,
          credits: Number(credits),
          amount_ils: Number(finalNis.toFixed(2)),
          note: fullNote,
        });
        if (data?.url) {
          // Redirect the user to CardCom's hosted page.
          window.location.href = data.url;
          return;
        }
        toast.error("CardCom לא הצליח ליצור עמוד תשלום");
        return;
      }
      await requestTopup(companyId, { credits: Number(credits), method, note: fullNote });
      toast.success(discount > 0
        ? `הבקשה נשלחה עם בקשת הנחה ${discount}%`
        : "הבקשה נשלחה — תאושר בקרוב ותעודכן היתרה", { duration: 5000 });
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    } finally { setBusy(false); }
  };

  const methodIcon = (k) => ({ credit_card: CreditCard, paypal: Wallet, bank_transfer: Landmark, bit: Smartphone, paybox: Smartphone }[k] || CreditCard);

  return (
    <div className="fixed inset-0 z-[10010] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-stone-900 rounded-2xl border border-emerald-500/30 w-full max-w-md max-h-[92vh] overflow-y-auto p-5 sm:p-6 shadow-2xl my-auto" onClick={(e) => e.stopPropagation()} dir="rtl" data-testid="add-credits-modal">
        <div className="flex items-center justify-between gap-2 mb-4">
          <h3 className="font-heading text-lg sm:text-xl font-extrabold text-white flex items-center gap-2 min-w-0 truncate">
            <Plus className="h-5 w-5 text-emerald-400 flex-shrink-0" />
            <span className="truncate">הוסף קרדיטים</span>
          </h3>
          <button onClick={onClose} className="h-8 w-8 flex-shrink-0 rounded-lg text-stone-400 hover:bg-white/10 inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-stone-400 mb-4">1 קרדיט = <strong className="text-white">{CREDIT_TO_NIS} ש"ח</strong></p>

        <div className="grid grid-cols-5 gap-1.5 mb-3">
          {presets.map((p) => (
            <button key={p} type="button" onClick={() => setCredits(p)}
              className={`py-2 rounded-lg text-xs font-bold border transition ${credits === p ? "bg-emerald-500/30 border-emerald-400 text-white" : "bg-stone-800 border-white/10 text-stone-300 hover:border-emerald-400/50"}`}
              data-testid={`add-credits-preset-${p}`}>{p}</button>
          ))}
        </div>

        <label className="block mb-4">
          <span className="text-[11px] font-bold text-stone-300">כמות קרדיטים</span>
          <input type="number" min="1" value={credits} onChange={(e) => setCredits(Number(e.target.value || 0))}
            className="mt-1 w-full bg-stone-800 border border-white/10 rounded-lg px-3 py-2 text-white text-base font-bold" dir="ltr"
            data-testid="add-credits-input" />
          <span className="text-[10px] text-stone-400 mt-1 block">≈ <strong className="text-emerald-300">{formatNis(creditsToNis(credits))}</strong></span>
        </label>

        <div className="mb-4">
          <span className="text-[11px] font-bold text-stone-300 block mb-2">שיטת תשלום</span>

          {/* Primary CTA: combined credit-card / Bit / PayPal — clicking
              this opens the CardCom hosted payment page in the same
              tab, which supports all three methods natively. */}
          <button
            type="button"
            onClick={() => setMethod("instant_card_bit_paypal")}
            className={`w-full flex items-center justify-between gap-2 px-3 py-3 rounded-lg border text-sm font-extrabold transition mb-2 ${
              method === "instant_card_bit_paypal"
                ? "bg-emerald-500/20 border-emerald-400 text-emerald-100 shadow-lg shadow-emerald-900/30"
                : "bg-stone-800 border-white/10 text-stone-200 hover:border-emerald-400/50"
            }`}
            data-testid="add-credits-method-instant"
          >
            <span className="inline-flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <Smartphone className="h-4 w-4 -ms-1" />
              <Wallet className="h-4 w-4 -ms-1" />
              <span>כרטיס אשראי · ביט · PayPal</span>
            </span>
            <span className="text-[10px] bg-emerald-500/30 text-emerald-100 px-2 py-0.5 rounded-full">תשלום מיידי</span>
          </button>

          {/* Secondary: bank transfer. PayBox intentionally hidden per
              owner request 2026-05-19 (will be re-enabled once a
              dedicated PayBox merchant flow is ready). */}
          <button
            type="button"
            onClick={() => setMethod("bank_transfer")}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-bold transition ${
              method === "bank_transfer"
                ? "bg-sky-500/20 border-sky-400 text-sky-100"
                : "bg-stone-800 border-white/10 text-stone-300 hover:border-sky-400/50"
            }`}
            data-testid="add-credits-method-bank"
          >
            <Landmark className="h-3.5 w-3.5" /> העברה בנקאית
          </button>

          {/* Bank-transfer details — exposed only when the user picks
              "bank_transfer". Mirrors the standard Israeli bank-info
              card (Discount Bank / branch / account). */}
          {method === "bank_transfer" && (
            <div className="mt-3 rounded-xl border border-sky-500/30 bg-sky-950/30 p-3 space-y-2 text-sm" data-testid="bank-transfer-details">
              <div className="text-[12px] font-extrabold text-sky-200 mb-1">פרטי חשבון להעברה</div>
              <div className="grid grid-cols-2 gap-y-1.5 text-[12px]">
                <span className="text-stone-400">בנק:</span>
                <span className="text-white font-bold">דיסקונט (11)</span>
                <span className="text-stone-400">סניף:</span>
                <span className="text-white font-bold tabular-nums">58</span>
                <span className="text-stone-400">מספר חשבון:</span>
                <span className="text-white font-extrabold tabular-nums">154867588</span>
                <span className="text-stone-400">שם המוטב:</span>
                <span className="text-white font-bold">ברק דדון</span>
              </div>
              <div className="border-t border-sky-700/30 pt-2 mt-2">
                <div className="text-[11px] text-amber-200 leading-snug">
                  💡 לאחר ביצוע ההעברה — שלח/י את האסמכתא להערה למטה ולחץ/י "שלח בקשה".
                  הקרדיטים ייטענו ברגע שנאמת.
                </div>
              </div>
            </div>
          )}
        </div>

        <label className="block mb-4">
          <span className="text-[11px] font-bold text-stone-300">הערה (אופציונלי)</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="פרטי תשלום, מספר העברה וכו'"
            className="mt-1 w-full bg-stone-800 border border-white/10 rounded-lg px-3 py-2 text-white text-xs" data-testid="add-credits-note" />
        </label>

        {/* Coupon-code gate + discount slider. The slider is FROZEN
            (visually faded + pointer-events disabled) until the user
            enters the correct code (1986) and clicks אישור. The code
            input sits ABOVE the frozen area with a helper note that
            directs them to a Flow Up team member to get the code. */}
        <div className="rounded-lg bg-stone-800/40 border border-stone-700 p-3 mb-4 space-y-2.5">
          {!couponUnlocked ? (
            <>
              <label className="text-[11px] font-bold text-stone-300 block">
                🎟️ קוד פתיחת קופון
              </label>
              <p className="text-[10px] text-stone-400 leading-relaxed">
                לקבלת הקוד — פנה לאיש צוות ב-Flow Up
              </p>
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value); setCouponError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); tryUnlockCoupon(); } }}
                  placeholder="הזן קוד"
                  className="flex-1 bg-stone-900 border border-white/15 rounded-lg px-3 py-2 text-white text-sm font-bold text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-fuchsia-500/50"
                  dir="ltr"
                  data-testid="coupon-code-input"
                />
                <button
                  type="button"
                  onClick={tryUnlockCoupon}
                  className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-xs font-extrabold px-4 rounded-lg transition"
                  data-testid="coupon-code-submit"
                >
                  אישור
                </button>
              </div>
              {couponError && <p className="text-[10px] text-rose-400 font-bold" data-testid="coupon-code-error">{couponError}</p>}
            </>
          ) : null}

          <div
            className={`rounded-lg p-3 transition-all ${
              couponUnlocked
                ? "bg-fuchsia-950/30 border border-fuchsia-500/30"
                : "bg-stone-900/40 border border-stone-700 opacity-50 pointer-events-none select-none"
            }`}
            data-testid="coupon-slider-block"
          >
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="topup-coupon" className="text-[11px] font-bold text-fuchsia-200 flex items-center gap-1">
                🎟️ קופון הנחה
                {!couponUnlocked && <span className="text-[9px] text-stone-500 mr-1">(נעול)</span>}
              </label>
              <span className="text-base font-extrabold text-fuchsia-100 tabular-nums">{discount}%</span>
            </div>
            <input
              id="topup-coupon"
              type="range" min={0} max={100} step={5}
              value={discount}
              onChange={(e) => setDiscount(Number(e.target.value))}
              disabled={!couponUnlocked}
              className="w-full accent-fuchsia-500"
              data-testid="add-credits-discount"
            />
            {discount > 0 && couponUnlocked && (
              <div className="text-[10px] text-fuchsia-200 mt-2 flex items-center justify-between">
                <span>סופי לתשלום: <strong className="text-white">{formatNis(creditsToNis(credits) * (1 - discount / 100))}</strong></span>
                <span className="text-emerald-300">חוסך: {formatNis(creditsToNis(credits) * (discount / 100))}</span>
              </div>
            )}
          </div>
        </div>

        {method === "instant_card_bit_paypal" ? (
          <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-lg p-2.5 mb-4 text-[11px] text-emerald-200 leading-relaxed">
            ✅ תועבר/י לדף תשלום מאובטח של CardCom. הקרדיטים ייטענו אוטומטית מיד עם ההצלחה.
          </div>
        ) : (
          <div className="bg-amber-950/30 border border-amber-500/30 rounded-lg p-2.5 mb-4 text-[11px] text-amber-200 leading-relaxed">
            ⚠ לאחר שליחת הבקשה — הקרדיטים ייטענו לחשבון מיד עם האישור.
          </div>
        )}

        <button onClick={submit} disabled={busy}
          className="w-full bg-gradient-to-l from-emerald-500 to-emerald-600 hover:opacity-90 disabled:opacity-50 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition"
          data-testid="add-credits-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> :
           method === "instant_card_bit_paypal" ? <><CreditCard className="h-4 w-4" /> לתשלום</> :
           <><Plus className="h-4 w-4" /> שלח בקשה</>}
        </button>
      </div>
    </div>
  );
}

// ============ History Modal ============
function HistoryModal({ items, loading, onClose }) {
  return (
    <div className="fixed inset-0 z-[10010] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-stone-900 rounded-2xl border border-fuchsia-500/30 w-full max-w-2xl max-h-[88vh] flex flex-col shadow-2xl my-auto" onClick={(e) => e.stopPropagation()} dir="rtl" data-testid="history-modal">
        <header className="flex items-center justify-between gap-2 p-5 border-b border-white/10">
          <h3 className="font-heading text-lg sm:text-xl font-extrabold text-white flex items-center gap-2 min-w-0 truncate">
            <History className="h-5 w-5 text-fuchsia-400 flex-shrink-0" />
            <span className="truncate">היסטוריית חיובים ופעולות עבודה</span>
          </h3>
          <button onClick={onClose} className="h-8 w-8 flex-shrink-0 rounded-lg text-stone-400 hover:bg-white/10 inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-10 text-stone-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-stone-500 text-sm">אין רשומות עדיין</div>
          ) : (
            <div className="divide-y divide-white/5">
              {items.map((h) => (
                <HistoryRow key={h.id} h={h} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HistoryRow({ h }) {
  const pos = h.delta > 0;
  const hasActivities = Array.isArray(h.activities) && h.activities.length > 0;
  const [expanded, setExpanded] = useState(false);
  return (
    <div data-testid={`history-row-${h.id}`}>
      <div className="flex items-center justify-between gap-3 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => hasActivities && setExpanded((v) => !v)}
              disabled={!hasActivities}
              className={`shrink-0 h-6 w-6 rounded-full inline-flex items-center justify-center transition ${
                hasActivities
                  ? "bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/25 cursor-pointer"
                  : "border border-white/5 text-stone-600 cursor-default"
              }`}
              title={hasActivities ? "הצג פירוט עבודה" : "אין פירוט"}
              data-testid={`history-expand-${h.id}`}
            >
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
            </button>
            <span className="text-sm text-white truncate">{h.label}</span>
            {hasActivities && !expanded && (
              <span className="text-[10px] font-bold text-fuchsia-300 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full px-1.5 py-0.5 shrink-0">
                {h.activities.length} פעולות
              </span>
            )}
          </div>
          <div className="text-[10px] text-stone-500 mt-0.5 mr-8">
            {new Date(h.created_at).toLocaleString("he-IL")}
          </div>
        </div>
        <div className={`text-base font-extrabold flex-shrink-0 ${pos ? "text-emerald-300" : "text-rose-300"}`}>
          {pos ? "+" : ""}{h.delta} <span className="text-[10px] text-stone-500">קרדיטים</span>
        </div>
      </div>
      {expanded && hasActivities && (
        <div className="mr-8 mb-3 rounded-xl border border-fuchsia-500/20 bg-fuchsia-500/5 p-3 space-y-1.5" data-testid={`history-activities-${h.id}`}>
          <div className="text-[11px] font-bold text-fuchsia-300 flex items-center gap-1 mb-1">
            <Sparkles className="h-3 w-3" />
            פירוט העבודה שבוצעה
          </div>
          {h.activities.map((a, idx) => {
            const summary = (a && (a.summary || a.label || a.description)) || "";
            const at = a && a.at ? new Date(a.at).toLocaleString("he-IL", { hour: "2-digit", minute: "2-digit" }) : "";
            if (!summary) return null;
            return (
              <div key={idx} className="flex items-start gap-2 text-[12px] text-stone-200" data-testid={`history-activity-${h.id}-${idx}`}>
                <span className="shrink-0 mt-1 h-1.5 w-1.5 rounded-full bg-fuchsia-400" />
                <span className="flex-1 leading-snug">{summary}</span>
                {at && <span className="shrink-0 text-[10px] text-stone-500 tabular-nums">{at}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VaultRow({ label, value, show, onCopy }) {
  const hasValue = !!value;
  return (
    <div className="flex items-center gap-2 rounded-lg bg-stone-800 border border-white/5 px-3 py-2">
      <span className="text-[11px] font-bold text-stone-300 w-32 flex-shrink-0">{label}</span>
      <span className="flex-1 min-w-0 text-[11px] font-mono text-stone-100 truncate" dir="ltr">
        {hasValue ? (show ? value : "•".repeat(Math.min(12, String(value).length))) : <em className="text-stone-500">— ריק —</em>}
      </span>
      {hasValue && (
        <button type="button" onClick={() => onCopy(value, label)} className="h-7 w-7 inline-flex items-center justify-center rounded-md text-stone-400 hover:text-pink-300 hover:bg-pink-500/10 transition" data-testid={`vault-copy-${label.replace(/\s/g, "-")}`}>
          <Copy className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function ServiceLink({ to, icon: Icon, label, external }) {
  return (
    <Link to={to} className="flex items-center gap-2 rounded-lg bg-stone-800 hover:bg-fuchsia-500/15 border border-white/5 hover:border-fuchsia-500/40 px-3 py-2 text-sm font-bold text-stone-100 hover:text-fuchsia-200 transition group">
      <Icon className="h-4 w-4 text-fuchsia-400 group-hover:text-fuchsia-300" />
      <span className="flex-1">{label}</span>
      {external && <ExternalLink className="h-3 w-3 text-stone-500" />}
    </Link>
  );
}
